import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CountryPhoneInput from "@/components/common/CountryPhoneInput";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    title: user?.title || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    full_name: user?.full_name || "",
    mobile: user?.mobile || "",
    organization: user?.organization || "",
    affiliation: user?.affiliation || "",
    city: user?.city || "",
    state: user?.state || "",
    country: user?.country || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      title: user?.title || "",
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      full_name: user?.full_name || "",
      mobile: user?.mobile || "",
      organization: user?.organization || "",
      affiliation: user?.affiliation || "",
      city: user?.city || "",
      state: user?.state || "",
      country: user?.country || "",
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();

    const mobileValue = (form.mobile || "").trim();
    if (!/^\+[1-9]\d{5,14}$/.test(mobileValue)) {
      toast.error("Please enter a valid mobile number in international format.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        full_name: [form.title, form.first_name, form.last_name].filter(Boolean).join(" ").trim(),
        title: (form.title || "").trim(),
        first_name: (form.first_name || "").trim(),
        last_name: (form.last_name || "").trim(),
        mobile: (form.mobile || "").trim().replace(/\s+/g, ""),
        organization: (form.organization || "").trim(),
        affiliation: (form.affiliation || "").trim(),
        city: (form.city || "").trim(),
        state: (form.state || "").trim(),
        country: (form.country || "").trim(),
      };
      await api.patch("/users/me", payload);
      await refresh();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <form onSubmit={save} className="sqit-card p-8 space-y-5" data-testid="profile-form">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Title</label>
            <select
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            >
              <option value="">Select title</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">First name</label>
            <input
              data-testid="profile-first-name"
              required
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Last name</label>
            <input
              data-testid="profile-last-name"
              required
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Mobile</label>
            <CountryPhoneInput
              data-testid="profile-mobile"
              required
              value={form.mobile}
              onChange={(value) => setForm({ ...form, mobile: value })}
              className="w-full"
              inputClassName="focus:ring-0"
              selectClassName="focus:ring-0"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Organization</label>
            <input
              data-testid="profile-organization"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Affiliation</label>
            <input
              data-testid="profile-affiliation"
              value={form.affiliation}
              onChange={(e) => setForm({ ...form, affiliation: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">City</label>
            <input
              data-testid="profile-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">State</label>
            <input
              data-testid="profile-state"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Country</label>
            <input
              data-testid="profile-country"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
        </div>
        <div className="pt-4 border-t border-[#E5E7EB]">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-gray-500 uppercase tracking-wider">Username</dt>
              <dd className="font-semibold text-gray-900">{user?.username}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 uppercase tracking-wider">Email</dt>
              <dd className="font-semibold text-gray-900">{user?.email}</dd>
            </div>
          </dl>
        </div>
        <button
          disabled={saving}
          data-testid="profile-save"
          className="rounded-md bg-[#0B6936] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#07522f] disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
